import { bookActions, favoriteActions } from '@/actions';
import Card from '@/components/elements/Card/Card';
import Companies from '@/components/elements/Card/Companies';
import EmployeeCard from '@/components/elements/Card/EmployeeCard';
import ServiceCard from '@/components/elements/Card/ServiceCard';
import { Button, Fab, LinkButton } from '@/components/elements/forms/buttons';
import SearchInput from '@/components/elements/forms/SearchInput/SearchInput';
import { ListItem } from '@/components/elements/lists';
import Logotype from '@/components/elements/Logotype';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import Rating from '@/components/elements/redesign/Rating/Rating';
import Icon from '@/components/icons/Icon';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import Breadcrumbs from '@/components/modules/breadcrumbs/Breadcrumbs';
import { EmployeeReviews } from '@/components/modules/employee';
import BookAppointment from '@/components/modules/modals/BookAppointment/BookAppointment';
import { useBookAppointmentContext } from '@/components/modules/modals/BookAppointment/BookAppointment.hooks';
import Address from '@/components/modules/place/Address';
import Contact from '@/components/modules/place/Contact';
import Links from '@/components/modules/place/Links';
import OpeningHours from '@/components/modules/place/OpeningHours';
import Photos from '@/components/modules/place/Photos';
import PlaceDetails from '@/components/modules/place/PlaceDetails';
import PlaceInfo from '@/components/modules/place/PlaceInfo';
import Seo from '@/components/modules/place/Seo';
import Services from '@/components/modules/place/Services';
import { InstagramFeed } from '@/components/modules/salon';
import Bundles from '@/components/modules/salon/Bundles';
import Certificates from '@/components/modules/salon/Certificates';
import EmptyState from '@/components/templates/EmptyState';
import { MEGA_MENU_NAVIGATION_FEATURE_FLAG } from '@/constants/experimentConstants';
import Reviews from '@/features/PlacePages/modules/Reviews';
import {
  classnames,
  getDetailsBreadcrumbs,
  hasFeatureSetting,
  isBookable,
  shouldDisplayReviews,
  trackMpEvent,
} from '@/helpers';
import {
  formatPhoneNumber,
  getGiftcardCheckoutLink,
  getGiftcardLabel,
  getMerchantKlarnaClientId,
  getOpenHoursStrings,
} from '@/helpers/placeHelperTS';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION } from '@/hooks/search/useSearch.constants';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useMobileView from '@/hooks/useMobileView';
import { __, _s, getLang } from '@/locale';
import { PLACE_IMPRESSION, placeService } from '@/services';
import { Place, PlaceEmployee } from '@/types/state/shared';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import FakeSearchForm from '../searchV2/modules/FakeSearchForm/FakeSearchForm';
import TabBar from './components/TabBar';
import { handleShowTabItemClick } from './helpers/navigation';
import { getCompanyWebsiteWithProcotol, getImages, getMatchedServicesTitle, mapOtherPlaces } from './helpers/placeData';
import { UsePlacePage } from './hooks/PlacePage.hooks';
import Categories from './modules/Categories';
import Header from './modules/Header';
import ListView from './modules/ListView';
import FavoritesLoginModal from './modules/modals/LoginModal';
import ServiceReadMoreModal from './modules/modals/ServiceReadMoreModal';

const baseTranslationKey = 'pages.places.$slug';

type PlacePageProps = {
  place: Place;
  isSistaminuten?: boolean;
};

const PlacePage = ({ place, isSistaminuten }: PlacePageProps) => {
  const { isMobileView } = useMobileView();
  const history = useHistory();
  const location = useLocation();
  const getVariant = useGetAmplitudeExperimentVariant();
  const searchParams = new URLSearchParams(location.search);
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.users?.user);
  const flags = useAppSelector((state) => state?.flags);
  const context = useBookAppointmentContext();

  const useMegaMenuFeature =
    flags?.[MEGA_MENU_NAVIGATION_FEATURE_FLAG]?.value === 'on' ||
    getVariant(MEGA_MENU_NAVIGATION_FEATURE_FLAG)?.value === 'on';

  // Refs
  const tooltipTargetRef = useRef(null);
  const headerTargetRef = useRef(null);
  const reviewRef = useRef(null);
  const serviesRef = useRef(null);
  const employeesRef = useRef(null);
  const placeInfoRef = useRef(null);
  const searchInputRef = useRef(null);

  const realHeaderPosition = useIntersectionObserver(headerTargetRef, {
    root: null,
    threshold: useMegaMenuFeature ? 0.1 : 1,
    rootMargin: '-64px 0px 0px 0px',
  });

  const headerPosition = headerTargetRef?.current && realHeaderPosition === null ? 'inView' : realHeaderPosition;

  const [readMore, setReadMore] = useState<{ title: string; text: string; id: number }>(undefined);
  const [showFullPhoneNumber, setShowFullPhoneNumber] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const {
    matchedServices,
    onRemoveSearch,
    search,
    showSearch,
    searchedBundles,
    searchedServices,
    onShowSearchInput,
    onSearch,
    onEmployeeClick,
    onCloseEmployeeReviews,
    employeeReviews,
    isSticky,
  } = UsePlacePage(place, headerTargetRef);

  const isMpReviews = !['1', 'true'].includes(searchParams.get('service-reviews'));
  const { about = {}, contact = {}, reviews = {} } = place;
  const {
    showBundlesOnTop = true,
    hideEmployees,
    displayCertificates,
    openingHoursExtraInformation,
  } = about.settings || {};
  const { stats = {}, employeeStats = null } = reviews || {};
  const { count, score } = stats;
  const hideStaff = Boolean(place.schedule) || hideEmployees;
  const placeImages = getImages(place);
  const breadcrumbs = getDetailsBreadcrumbs(place);
  const haveReviews = Boolean(place.reviews?.stats?.count > 4);
  const placeEmployeesCustomTitle =
    place && place.about && place.about.employeesAlias
      ? place.about.employeesAlias
      : _s(`${baseTranslationKey}.employeesTitle`);

  const serviceCategory = about.category?.main ?? about.category?.sub;
  const companyPhoneNumber = contact.phone ? formatPhoneNumber(contact.phone) : undefined;
  const companyEmail = contact.email;

  const hasCollapsedSetting = hasFeatureSetting(place, 'collapsed_services');
  const showListView = place.schedule && place.services && place.services.length;
  const services = place.services;
  const employees = place.employees;
  const missingServies = !services?.some((service) => service.services.length > 0);
  const giftcardLabel = getGiftcardLabel(place);
  const giftcardLink = getGiftcardCheckoutLink(place);
  const companySocials = {
    instagram: {
      name: place.contact?.instagram,
      link: place.contact?.instagram ? `https://www.instagram.com/${place.contact.instagram}` : undefined,
    },
    facebook: {
      name: place.contact?.facebook,
      link: place.contact?.facebook ? `https://www.facebook.com/${place.contact.facebook}` : undefined,
    },
    website: getCompanyWebsiteWithProcotol(place),
  };
  const haveCompanySocials = Boolean(
    companySocials.instagram.name || companySocials.facebook.name || companySocials.website,
  );
  const openingHours = getOpenHoursStrings(place.program);
  const showInstagramFeed =
    about.settings?.subscriptionType === 'growth' && contact?.instagram && Boolean(about.instagramFeed.length);

  useEffect(() => {
    if (showSearch && searchInputRef?.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const goBack = () => {
    if (location.state?.fromList) {
      history.goBack();
    } else {
      history.replace(`/${DEFAULT_SEARCH_KEYWORD}/${DEFAULT_SEARCH_LOCATION}`);
    }
  };

  const handleSearchInputBlur = () => {
    if (searchInputRef?.current && !search) {
      onRemoveSearch();
    }
  };

  const handleShowFullPhoneNumber = () => {
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_SHOW_PHONE_NUMBER_CLICK);
    setShowFullPhoneNumber(true);
  };

  const handlePhoneNumberClick = async () => {
    await placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_PHONE_NUMBER_CLICK);
    window.location.href = `tel:${companyPhoneNumber.fullNumber}`;
  };

  const handleClickReadMore = (title: string, text: string, id: number) => {
    setReadMore({ title, text, id });
  };

  const handleCloseReadMore = () => {
    setReadMore(undefined);
  };

  const handleAddToFavorites = () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    const isPlaceAlreadyFavorite = user.favorites?.indexOf(place.id) !== -1;

    trackMpEvent(isPlaceAlreadyFavorite ? 'favorite_removed' : 'favorite_added', {
      screen_name: 'company_details',
      company_id: place.id,
    });

    const dispatchAction = isPlaceAlreadyFavorite
      ? favoriteActions.remove(place.id, user)
      : favoriteActions.add(place.id, user);

    // @ts-ignore
    dispatch(dispatchAction).then(() => {
      toast(({ closeToast }) => (
        <Snackbar
          type="success"
          onClose={closeToast}
          label={
            !isPlaceAlreadyFavorite
              ? __(`${baseTranslationKey}.favorites.added`)
              : __(`${baseTranslationKey}.favorites.removed`)
          }
        />
      ));
    });
  };

  const handleCloseLoginModal = () => {
    setShowLoginModal(false);
  };

  const handleExternalWebsiteClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_EXTERNAL_BUSINESS_LINK_CLICK);
  };

  const handleRowGiftcardClick = () => {
    trackMpEvent('buy_gift_card_clicked', {
      screen_name: 'company_details',
      company_id: place.id,
      trigger_source: 'company_details',
    });

    history.push({
      pathname: giftcardLink,
    });
  };

  const handleLinkGiftcardClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    trackMpEvent('buy_gift_card_clicked', {
      screen_name: 'company_details',
      company_id: place.id,
      trigger_source: 'company_details',
    });
  };

  const handlePickService = (employee: PlaceEmployee) => {
    onCloseEmployeeReviews();
    context.updateView('pickServices');
    dispatch(bookActions.clearBook());
    dispatch(bookActions.removeEmployee());
    // @ts-ignore
    dispatch(bookActions.pickEmployee(employee.id, employee.about.priceListId));
    dispatch(bookActions.keepEmployee());
    dispatch(bookActions.addPlace(place));
    context.open();
  };

  const getEmployeeCardProps = (employee: PlaceEmployee) => {
    const employeeRating = employeeStats?.[employee.id]?.score ? employeeStats[employee.id] : undefined;
    const showBookButton = isBookable({ employee, place });

    return {
      employee: employee,
      stats: employeeRating,
      onClick: showBookButton || employeeRating ? () => onEmployeeClick(employee, employeeRating) : undefined,
      rightSlot: showBookButton ? (
        <Button
          label={__('chooseService')}
          data-cy="chooseEmployeeButton"
          onClick={(e) => handlePickService(employee)}
          size="sm"
          variant={'primary'}
        />
      ) : undefined,
    };
  };

  const showNonStickyTabBar = () => {
    if (headerPosition === 'inView' || headerPosition === 'belowViewport') {
      return false;
    }

    if (isMobileView && !isSticky) {
      return false;
    }

    if (!isMobileView && !headerTargetRef.current) {
      return false;
    }

    return true;
  };

  return (
    <>
      <PageViewLayout
        back
        hideBottomNavBar
        title={about.name}
        type="mainView"
        onBackButtonClick={goBack}
        wrapperClass="bg-surface_mp"
        /**
         * Hide bokadirekt logo on mobile view
         */
        leftSlot={
          isMobileView ? (
            <Fragment>
              {isSticky && useMegaMenuFeature && (
                <div className="gap-xxs flex flex-col justify-center">
                  <div className="text-l">{about.name}</div>
                  <Rating count={count ?? 0} rating={score} size={'sm'} />
                </div>
              )}
            </Fragment>
          ) : (
            <Link to="/" aria-label="Bokadirekt" className="hidden lg:block">
              <Logotype className="not-sr-only" variant="logo" style={{ height: 22 }} />
            </Link>
          )
        }
        middleSlot={(() => {
          if (!useMegaMenuFeature) {
            return null;
          }

          if (!isMobileView) return <FakeSearchForm isSistaminuten={isSistaminuten} />;

          if (!isSticky) {
            return <FakeSearchForm isSistaminuten={isSistaminuten} />;
          }
          return null;
        })()}
        /**
         * Move tab bar to bottomslot when scrolled down
         */
        {...((showNonStickyTabBar() || (isSticky && isMobileView)) &&
          useMegaMenuFeature && {
            bottomSlot: (
              <TabBar
                top
                showReviews={shouldDisplayReviews(place)}
                hideEmployees={hideStaff}
                placeId={place.id}
                onFavoriteClick={handleAddToFavorites}
                className={classnames(
                  isMobileView && 'h-[64px]',
                  !isMobileView && 'px-md h-[72px]',
                  'flex justify-center',
                )}
                placeName={about.name}
                reviews={{ count: count ?? 0, rating: score }}
              />
            ),
          })}>
        <div className="text-fg_primary flex justify-center">
          <div
            className={`px-md pt-md pb-3xl flex w-full max-w-[1040px] flex-col ${
              isMobileView ? 'gap-2xl' : 'gap-4xl'
            }`}>
            <Breadcrumbs items={breadcrumbs} className="w-full text-sm" />

            <Header
              place={place}
              onReviewsClick={() => handleShowTabItemClick(reviewRef)}
              onFavoriteClick={handleAddToFavorites}
            />

            {getMerchantKlarnaClientId(place) && (
              // @ts-ignore
              <klarna-placement
                id="klarna-placement"
                data-key="top-strip-promotion-auto-size"
                data-locale={getLang() === 'sv' ? 'sv-SE' : 'en-SE'}
              />
            )}

            <div
              className={classnames(
                `border-border_regular fixed left-0 top-[64px] z-[99] w-screen border-t transition-opacity duration-200`,
                showNonStickyTabBar() ? 'visible opacity-100' : 'invisible opacity-0',
              )}>
              <TabBar
                className={classnames(
                  isMobileView && 'px-lg h-[64px]',
                  !isMobileView && 'px-md h-[72px]',
                  'flex justify-center',
                  'drop-shadow-xs',
                )}
                top
                showReviews={shouldDisplayReviews(place)}
                hideEmployees={hideStaff}
                placeId={place.id}
                onFavoriteClick={handleAddToFavorites}
                placeName={about.name}
                reviews={{ count: count ?? 0, rating: score }}
              />
            </div>

            <div
              ref={headerTargetRef}
              style={{ top: 64, ...(isMobileView && { height: 64 }) }}
              className={classnames(
                isMobileView && 'sticky',
                isSticky && isMobileView ? '-ml-[16px] w-[calc(100%+32px)]' : '',
                !useMegaMenuFeature && 'z-[98]',
              )}>
              <TabBar
                top={isSticky && isMobileView}
                className={classnames(
                  isMobileView && 'px-lg h-[64px]',
                  !isMobileView && 'px-md h-[72px]',
                  isSticky && isMobileView ? 'flex justify-center' : 'border-border_regular rounded-lg border-b',
                  'drop-shadow-xs',
                )}
                placeId={place.id}
                hideEmployees={hideStaff}
                showReviews={shouldDisplayReviews(place)}
                onFavoriteClick={handleAddToFavorites}
                reviews={{ count: count ?? 0, rating: score }}
              />
            </div>

            {[1, 3].indexOf(placeImages?.length) === -1 && <Photos photos={placeImages} place={place} />}

            <div className={`flex ${isMobileView ? 'gap-2xl flex-col' : 'gap-md justify-between'}`}>
              <div className={`flex min-w-0 flex-1 flex-col ${isMobileView ? 'gap-2xl' : 'gap-4xl'}`}>
                {[1, 3].indexOf(placeImages?.length) !== -1 && placeImages?.length > 0 && (
                  <Photos photos={placeImages} place={place} />
                )}

                {matchedServices?.length > 0 && (
                  <div className="flex flex-col">
                    <ListItem
                      type="div"
                      leftPadding={false}
                      leftSlot={<Icon variant="search-check" size="md" />}
                      rightSlot={<span>{getMatchedServicesTitle({ ...location.state?.searchParams })}</span>}>
                      <h2 className="text-h-m">{_s(`${baseTranslationKey}.matchedServices.title`)}</h2>
                    </ListItem>

                    <div ref={serviesRef} id="services" className="scroll-mt-[144px]">
                      <Services
                        place={place}
                        services={matchedServices}
                        _expanded={true}
                        handleClickReadMore={handleClickReadMore}
                      />
                    </div>
                  </div>
                )}

                {giftcardLink && !isSistaminuten && (
                  <div id="giftCards">
                    <ListItem leftPadding={false} type="div" leftSlot={<Icon variant="gift" size="md" />}>
                      <h2 className="text-h-m">{_s('GiftCard')}</h2>
                    </ListItem>
                    <ServiceCard
                      name={giftcardLabel}
                      icon="gift-card"
                      description="Ge bort egentid, du väljer själv belopp"
                      price={{ type: 'optional', value: [0] }}
                      onRowClick={handleRowGiftcardClick}
                      rightSlot={
                        <LinkButton
                          to={giftcardLink}
                          onClick={handleLinkGiftcardClick}
                          variant="primary"
                          size={isMobileView ? 'md' : 'sm'}
                          className="flex max-w-[75px] items-center">
                          {isMobileView ? _s('Buy') : _s(`${baseTranslationKey}.buyGiftcardCta`)}
                        </LinkButton>
                      }
                    />
                  </div>
                )}

                <div className="flex flex-col">
                  {!missingServies && !showListView && (
                    <ListItem
                      type="div"
                      leftPadding={false}
                      leftSlot={<Icon variant="tags" size="md" />}
                      rightSlot={
                        !showSearch && (
                          <Fab
                            size="sm"
                            variant="secondary"
                            aria-label={_s(`${baseTranslationKey}.search.label`)}
                            onClick={onShowSearchInput}
                            rightIcon={<Icon variant="search" color="fg_primary" />}
                          />
                        )
                      }>
                      <div className="flex items-center justify-between">
                        {(!showSearch || (showSearch && !isMobileView)) && (
                          <h2 className="text-h-m">{_s(`${baseTranslationKey}.servicesTitle`)}</h2>
                        )}
                        {showSearch && (
                          <div className={isMobileView ? 'flex-1' : ''}>
                            <SearchInput
                              value={search}
                              onChange={onSearch}
                              onRemove={onRemoveSearch}
                              onBlur={handleSearchInputBlur}
                              ref={searchInputRef}
                              placeholder={_s(`${baseTranslationKey}.search.placeholder`)}></SearchInput>
                          </div>
                        )}
                      </div>
                    </ListItem>
                  )}

                  {!missingServies && showListView && (
                    <ListItem type="div" leftPadding={false} leftSlot={<Icon variant="tags" size="md" />}>
                      <h2 className="text-h-m">{_s(`${baseTranslationKey}.servicesTitle`)}</h2>
                    </ListItem>
                  )}

                  <div ref={serviesRef} id="services" className="scroll-mt-[144px]">
                    {missingServies && (
                      <Card size={isMobileView ? 'lg' : 'xl'}>
                        <div className="gap-2xl flex flex-col">
                          <div className="flex items-center justify-between">
                            <h2 className="text-h-s">{_s(`${baseTranslationKey}.servicesCard.title`)}</h2>
                          </div>
                          <EmptyState
                            title={_s(`${baseTranslationKey}.servicesCard.emptyState.title`)}
                            icon={<img src="/images/illustrations/empty.svg" alt="" />}
                          />
                        </div>
                      </Card>
                    )}

                    {!isSistaminuten && showBundlesOnTop && !Boolean(search) && (
                      <Bundles isOpen={!hasCollapsedSetting} place={place} newPlacePage />
                    )}

                    {!isSistaminuten && !showListView && Boolean(searchedBundles.length) && (
                      <Bundles
                        isOpen={!hasCollapsedSetting}
                        title={_s(`${baseTranslationKey}.search.matchedBundles`)}
                        place={{ ...place, bookingBundles: searchedBundles }}
                        newPlacePage
                      />
                    )}

                    {!showListView && Boolean(searchedServices.length) && (
                      <Services
                        place={place}
                        categoryName={_s(`${baseTranslationKey}.search.matchedServices`)}
                        services={searchedServices}
                        handleClickReadMore={handleClickReadMore}
                        _expanded={true}
                      />
                    )}

                    {!showListView && (Boolean(searchedServices.length) || Boolean(searchedBundles.length)) && (
                      <Button
                        label={_s(`${baseTranslationKey}.search.showAllCta`)}
                        variant="link"
                        onClick={onRemoveSearch}
                        size="md"
                      />
                    )}

                    {Boolean(search) && !Boolean(searchedBundles.length) && !Boolean(searchedServices.length) && (
                      <Card size={isMobileView ? 'lg' : 'xl'}>
                        <div className="gap-2xl flex flex-col">
                          <EmptyState
                            body={_s(`${baseTranslationKey}.search.emptyState.body`, {
                              search: search,
                            })}
                            title={_s(`${baseTranslationKey}.search.emptyState.title`)}
                            icon={<img src="/images/illustrations/empty.svg" alt="" />}
                            cta={
                              <Button
                                label={_s(`${baseTranslationKey}.search.showAllCta`)}
                                variant="link"
                                onClick={onRemoveSearch}
                                size="md"
                              />
                            }
                          />
                        </div>
                      </Card>
                    )}

                    {!missingServies && !showListView && !Boolean(search) && (
                      <Categories onClickReadMore={handleClickReadMore} place={place} />
                    )}

                    {showListView && <ListView place={place} />}

                    {!isSistaminuten && !showBundlesOnTop && !Boolean(search) && (
                      <Bundles isOpen={!hasCollapsedSetting} place={place} newPlacePage />
                    )}
                  </div>
                </div>

                {!hideStaff && (
                  <div id="staff" className="gap-md flex scroll-mt-[144px] flex-col" ref={employeesRef}>
                    <ListItem type="div" leftPadding={false} leftSlot={<Icon variant="users" size="md" />}>
                      <h2 className="text-h-m">{placeEmployeesCustomTitle}</h2>
                    </ListItem>
                    {employees.length > 0 &&
                      employees
                        .filter((employee) => {
                          return !employee.about?.inactive;
                        })
                        .map((employee, index) => {
                          return <EmployeeCard key={index} {...getEmployeeCardProps(employee)} />;
                        })}
                  </div>
                )}

                {isMobileView && shouldDisplayReviews(place) && (
                  <>
                    <div className="relative" ref={tooltipTargetRef}></div>
                    <div id="reviews" ref={reviewRef} className="scroll-mt-[144px]">
                      {haveReviews && (
                        <Reviews
                          place={place}
                          hideStaff={false}
                          isMpReviews={isMpReviews}
                          tooltipTargetRef={tooltipTargetRef}
                        />
                      )}
                      {!haveReviews && (
                        <>
                          <Card size={'lg'}>
                            <div className="gap-2xl flex flex-col">
                              <div className="flex items-center justify-between">
                                <h2 className="text-h-s">{'Betyg och recensioner'}</h2>
                              </div>
                              <EmptyState
                                title={_s('noReviews')}
                                icon={<img src="/images/illustrations/empty.svg" alt="" />}
                              />
                            </div>
                          </Card>
                        </>
                      )}
                    </div>
                  </>
                )}

                {isMobileView && (
                  <>
                    <PlaceDetails place={place} isVerified={true} isBasic={false} isPremium={true} />
                    {(companyEmail || companyPhoneNumber) && (
                      <Contact
                        placeId={place.id}
                        email={companyEmail}
                        isVerified={true}
                        onShowFullPhoneNumber={handleShowFullPhoneNumber}
                        onPhoneNumberClick={handlePhoneNumberClick}
                        phone={
                          companyPhoneNumber
                            ? showFullPhoneNumber
                              ? companyPhoneNumber.fullNumber
                              : companyPhoneNumber.hiddenNumber
                            : undefined
                        }
                        fullNumber={showFullPhoneNumber}
                      />
                    )}

                    {haveCompanySocials && (
                      <Links placeId={place.id} onWebsiteClick={handleExternalWebsiteClick} {...companySocials} />
                    )}

                    <Address place={place} />
                    {openingHours && (
                      <OpeningHours openingHours={openingHours} additionalInfo={openingHoursExtraInformation} />
                    )}
                  </>
                )}

                {!isMobileView && shouldDisplayReviews(place) && (
                  <>
                    <div className="relative" ref={tooltipTargetRef}></div>
                    <div id="reviews" ref={reviewRef} className="scroll-mt-[144px]">
                      {haveReviews && (
                        <Reviews
                          place={place}
                          hideStaff={false}
                          isMpReviews={isMpReviews}
                          tooltipTargetRef={tooltipTargetRef}
                        />
                      )}
                      {!haveReviews && (
                        <Card size={'xl'}>
                          <div className="gap-2xl flex flex-col">
                            <div className="flex items-center justify-between">
                              <h2 className="text-h-s">{'Betyg och recensioner'}</h2>
                            </div>
                            <EmptyState
                              title={_s('noReviews')}
                              icon={<img src="/images/illustrations/empty.svg" alt="" />}
                            />
                          </div>
                        </Card>
                      )}
                    </div>
                  </>
                )}

                <div id="about" ref={placeInfoRef} className="scroll-mt-[144px]">
                  <PlaceInfo place={place} category={serviceCategory} isVerified={true} year={undefined} />
                </div>

                {displayCertificates && (
                  <div id="certificates" className="scroll-mt-[144px]">
                    <Card size={'xl'}>
                      <Certificates employees={place.employees} />
                    </Card>
                  </div>
                )}

                {showInstagramFeed && (
                  <div className="py-6">
                    <InstagramFeed profile={contact.instagram} media={about.instagramFeed} />
                  </div>
                )}
                {place.locations?.length > 0 && about.salonId && (
                  <Companies
                    companies={mapOtherPlaces(place.locations, place.id)}
                    bottomSlot={
                      <div className="pt-md flex justify-start">
                        <LinkButton
                          className={'!pl-0'}
                          to={`/goto/${about.salonId}`}
                          label={'Visa alla addresser'}
                          size="md"
                        />
                      </div>
                    }
                    title={{
                      bold: _s(`otherLocations`),
                    }}
                  />
                )}
              </div>

              <div className={`flex flex-col ${isMobileView ? 'gap-2xl' : 'gap-md max-w-[336px]'}`}>
                {!isMobileView && (
                  <>
                    <PlaceDetails place={place} isVerified={true} isBasic={false} isPremium={true} />
                    {openingHours && (
                      <OpeningHours openingHours={openingHours} additionalInfo={openingHoursExtraInformation} />
                    )}
                    {(companyEmail || companyPhoneNumber) && (
                      <Contact
                        placeId={place.id}
                        email={companyEmail}
                        isVerified={true}
                        onShowFullPhoneNumber={handleShowFullPhoneNumber}
                        onPhoneNumberClick={handlePhoneNumberClick}
                        phone={
                          companyPhoneNumber
                            ? showFullPhoneNumber
                              ? companyPhoneNumber.fullNumber
                              : companyPhoneNumber.hiddenNumber
                            : undefined
                        }
                        fullNumber={showFullPhoneNumber}
                      />
                    )}
                    {haveCompanySocials && (
                      <Links placeId={place.id} onWebsiteClick={handleExternalWebsiteClick} {...companySocials} />
                    )}
                    <Address place={place} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <BookAppointment />

        <FavoritesLoginModal onClose={handleCloseLoginModal} isOpen={showLoginModal} />

        <ServiceReadMoreModal
          isOpen={Boolean(readMore?.text)}
          onClose={handleCloseReadMore}
          place={place}
          id={readMore?.id}
          text={readMore?.text}
          title={readMore?.title}
        />

        {employeeReviews && (
          <EmployeeReviews
            isOpen={true}
            close={onCloseEmployeeReviews}
            employee={employeeReviews.employee}
            place={place}
            rating={employeeReviews.rating}
            isMpReviews={isMpReviews}
            newPlacePage
            pickService={() =>
              isBookable({ place, employee: employeeReviews.employee })
                ? handlePickService(employeeReviews.employee)
                : null
            }
          />
        )}
      </PageViewLayout>
      {Seo({ place: place, isSistaminuten: isSistaminuten })}
    </>
  );
};

export default PlacePage;
